<template>
  <div>
    <h2>How are computed properties used in Vue.js?</h2>
    <br />
    <ul>
      <li>
        Updating a large amount of information while a user is typing, such as
        filtering a list
      </li>
      <li>Gathering information from your Vuex store</li>
      <li>Form validation</li>
      <li>
        Data visualizations that change depending on what the user needs to see
      </li>
    </ul>
    <br />

    <h3>Filtering data</h3>
    <pre>
      <code>&lt;template&gt;
  &lt;v-card max-width="374" dark color="#385F73"&gt;
      &lt;v-list-item v-for="name in names" :key="name"&gt;
        &lt;v-list-item-title&gt;{{ testString }}&lt;/v-list-item-title&gt;
      &lt;/v-list-item&gt;
    &lt;/v-card&gt;
&lt;/template&gt;
&lt;script&gt;
  export default {
    name: "FilteringData", 
    data() {
      return {
        names: [
          'Vin Diesel',
          'Paul Walker',
          'Robert Downey Jr',
          'Chris Evans',
          'Chris Hemsworth',
          'Scarlett Johnson',
          'Brad Pitt',
          'Mark Ruffalo',
          'Dwayne Johnson',
          'Nick Fury',
          'Karthic Swaminathan'
        ],
      }
    },
  };
&lt;/script&gt;</code>
    </pre>

    <h2>Output</h2>
    <br />
    <v-card max-width="374" dark color="#385F73">
      <v-list-item v-for="name in names" :key="name">
        <v-list-item-title>{{ name }}</v-list-item-title>
      </v-list-item>
    </v-card>
    <br/>
    <p>
      Now let’s create a filter for those names. We’ll start by creating an input with v-model that will originally be an empty string, but we’ll eventually use to match and filter through our list. We’ll call this property findName and you can see it referenced both on the input and in the data.
    </p>

    <pre>
      <code>&lt;v-text-field
  class="pt-10 ml-5 mr-5"
  v-model="findName"
  label="Filter"
&gt;&lt;/v-text-field&gt;</code>
    </pre>

    <pre>
      <code>data() {
  return {
    findName: '',
    names: [
      'Vin Diesel',
      'Paul Walker',
      ...
    ]
  }
}</code>
    </pre>
    <p>
      Now, we can create the computed property that will filter all of the names based on what the user has typed into the input, so anything in our findName property. You’ll note that I’m using regex here to make sure that mismatched capitalization doesn’t matter, as users will typically not capitalize as they type.
    </p>

     <pre>
      <code>computed: {
  filteredNames() {
    let filter = new RegExp(this.findName, 'i')
    return this.names.filter(el => el.match(filter))
  }
}</code>
</pre>
<p>Now the entire code will be look like this,</p>
 <pre>
      <code>&lt;template&gt;
  &lt;v-card max-width="374" dark color="#385F73"&gt;
      &lt;v-list-item v-for="name in filteredNames" :key="name"&gt;
        &lt;v-list-item-title&gt;{{ testString }}&lt;/v-list-item-title&gt;
      &lt;/v-list-item&gt;
    &lt;/v-card&gt;
&lt;/template&gt;
&lt;script&gt;
  export default {
    name: "FilteringData", 
    findName: '',
    data() {
      return {
        names: [
          'Vin Diesel',
          'Paul Walker',
          'Robert Downey Jr',
          'Chris Evans',
          'Chris Hemsworth',
          'Scarlett Johnson',
          'Brad Pitt',
          'Mark Ruffalo',
          'Dwayne Johnson',
          'Nick Fury',
          'Karthic Swaminathan'
        ],
      }
    },
    computed: {
      filteredNames() {
        let filter = new RegExp(this.findName, 'i')
        return this.names.filter(el => el.match(filter))
      }
    }
  };
&lt;/script&gt;</code>
    </pre>

<h2>Output</h2>
    <br />
    <v-card max-width="374" dark color="#385F73">
      <v-text-field
          class="pt-10 ml-5 mr-5"
          v-model="findName"
          label="Filter"
      ></v-text-field>
      <v-list-item v-for="name in filteredNames" :key="name">
        <v-list-item-title>{{ name }}</v-list-item-title>
      </v-list-item>
    </v-card>
    <br/>

  </div>
</template>
<script>
export default {
  name: "ComputedUsage",
  data() {
    return {
      testString: "{{name}}",
      findName: "",
      names: [
        "Vin Diesel",
        "Paul Walker",
        "Robert Downey Jr",
        "Chris Evans",
        "Chris Hemsworth",
        "Scarlett Johnson",
        "Brad Pitt",
        "Mark Ruffalo",
        "Dwayne Johnson",
        "Nick Fury",
        "Karthic Swaminathan",
      ],
    };
  },
  computed: {
    filteredNames() {
      let filter = new RegExp(this.findName, "i");
      return this.names.filter((el) => el.match(filter));
    },
  },
};
</script>
